import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LostDialog } from "@/components/workflow/LostDialog";

export const WORK_STATUS_LABELS = { NEW: "New", IN_PROGRESS: "In progress", WAITING_FOR_INFO: "Waiting for more information", OPTIONS_SENT: "Options sent", CONFIRMED: "Confirmed", WON: "Won", LOST: "Lost", ARCHIVED: "Archived" };
export const TYPE_LABELS = { NEW: "New enquiry", MOVE: "Move", EXTENSION: "Extension", CANCELLATION: "Cancellation", AMENDMENT: "Amendment", ADDITIONAL_SERVICES: "Additional services" };
const UNASSIGNED = "__none__";

export function formatWait(seconds) {
  if (seconds == null) return "—";
  const h = Math.floor(seconds / 3600), m = Math.floor((seconds % 3600) / 60);
  if (h >= 48) return `${Math.floor(h / 24)}d ${h % 24}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// Permission-checked: the API refuses anything outside the Control Centre; the controls render only for staff and only on live items.
export function InlineAssign({ item, staff, canEdit, onChanged }) {
  const [busy, setBusy] = useState(false);
  const change = async (v) => {
    setBusy(true);
    const path = item.kind === "ENQUIRY" ? `/workflow/enquiries/${item.id}/assign` : `/workflow/change-requests/${item.id}/assign`;
    try { await api.post(path, { staff_user_id: v === UNASSIGNED ? null : v }); toast.success(v === UNASSIGNED ? "Unassigned" : "Assigned"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not assign"); }
    finally { setBusy(false); }
  };
  if (!canEdit) return <span data-testid={`wf-assignee-${item.id}`} className="text-[13px] text-jigsaw-navy">{item.assignee_name || "Unassigned"}</span>;
  return (
    <Select value={item.assignee_id || UNASSIGNED} onValueChange={change} disabled={busy}>
      <SelectTrigger data-testid={`wf-assign-${item.id}`} className="h-8 w-full min-w-[9rem] text-[13px]"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
        {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

// `statusOptions` come from /workflow/meta (Admin-maintained labels + active flags); codes and transitions stay fixed here.
export function InlineStatus({ item, canEdit, lostReasons, statusOptions = [], onChanged }) {
  const [busy, setBusy] = useState(false);
  const [lostOpen, setLostOpen] = useState(false);
  const labels = { ...WORK_STATUS_LABELS, ...Object.fromEntries(statusOptions.map((o) => [o.code, o.label])) };
  const inactive = new Set(statusOptions.filter((o) => !o.is_active).map((o) => o.code));
  const closed = ["WON", "LOST", "ARCHIVED"].includes(item.work_status);
  const options = (closed ? (item.work_status === "ARCHIVED" ? [] : ["ARCHIVED"]) : ["NEW", "IN_PROGRESS", "WAITING_FOR_INFO", "OPTIONS_SENT", "CONFIRMED", "LOST"]).filter((o) => !inactive.has(o));
  const submit = async (body) => {
    setBusy(true);
    try { await api.post(`/workflow/enquiries/${item.id}/work-status`, body); toast.success(`Status: ${labels[body.work_status]}`); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Status change refused"); }
    finally { setBusy(false); }
  };
  const REQUEST_LABELS = { PENDING: "Pending decision", DECIDING: "Being decided", APPLIED: "Applied", REJECTED: "Declined" };
  const text = item.kind === "REQUEST" ? `${REQUEST_LABELS[item.pipeline_status] || item.pipeline_status}${item.agreement_state ? ` · ${item.agreement_state.toLowerCase().replace(/_/g, " ")}` : ""}` : (labels[item.work_status] || item.work_status);
  const label = <span data-testid={`wf-status-${item.id}`} className="text-[13px] text-jigsaw-navy">{text}</span>;
  if (!canEdit || item.kind !== "ENQUIRY" || !options.length) return label;
  return (
    <>
      <Select value={item.work_status} onValueChange={(v) => (v === "LOST" ? setLostOpen(true) : submit({ work_status: v }))} disabled={busy}>
        <SelectTrigger data-testid={`wf-status-select-${item.id}`} className="h-8 w-full min-w-[10rem] text-[13px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          {!options.includes(item.work_status) && <SelectItem value={item.work_status} disabled>{labels[item.work_status] || item.work_status}</SelectItem>}
          {options.map((o) => <SelectItem key={o} value={o}>{labels[o]}</SelectItem>)}
        </SelectContent>
      </Select>
      <LostDialog open={lostOpen} onOpenChange={setLostOpen} reasons={lostReasons} reference={item.reference} onSubmit={(reason, note) => { setLostOpen(false); submit({ work_status: "LOST", lost_reason: reason, note }); }} />
    </>
  );
}
